/**
 * Settle up: record a payment that clears a balance.
 *
 * Settlements are append-only and REDUCE the balance rather than editing
 * history (docs/01 §7.2), so nothing here rewrites an expense. Recording a
 * smaller amount than the balance is allowed and simply leaves the remainder
 * outstanding — people hand over what they have on them.
 *
 * The direction is fixed: this records what YOU paid someone else. The outbox
 * stamps `from` with the acting member (`mutations.settle`), so a payment
 * someone else made can only be recorded honestly on their own phone.
 */

import { type FormEvent, useMemo, useState } from 'react'
import {
  type MemberId,
  type PaymentMethod,
  type Piastres,
  type TrustedClock,
  piastres,
} from '@office/domain'
import { amountToSettle, computeLedger, memberIdOf, pairBalance } from '@office/domain/money'
import type { MoneyActions } from './actions.js'
import { type MoneySnapshot, guestPredicate, nameOf } from './data.js'
import { amountInputValue, formatInstant, parseAmount } from './format.js'
import { AmountInput, Banner, Empty, Money, RadioGroup } from './ui.js'
import './money.css'

const METHODS: readonly { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
]

export interface SettleUpFormProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  actorId: MemberId
  actions: MoneyActions
  /** Preselect a counterparty, e.g. from a ledger row's "Settle up" button. */
  initialTo?: MemberId
  /**
   * The amount that row suggested. It is NOT always the pairwise balance: in
   * the simplified view the transfer that clears the ledger can be to someone
   * you owe nothing pairwise, and recomputing the prefill from the pair would
   * silently offer zero for a payment the plan says to make.
   */
  initialAmount?: Piastres
  onDone?: () => void
}

export function SettleUpForm({
  snapshot,
  clock,
  actorId,
  actions,
  initialTo,
  initialAmount,
  onDone,
}: SettleUpFormProps) {
  const now = clock.now()
  const ledger = useMemo(
    () => computeLedger(snapshot.expenses, snapshot.settlements, { isGuest: guestPredicate(snapshot) }),
    [snapshot],
  )

  /** Only owners can be settled with. A guest has no ongoing balance to clear. */
  const counterparties = snapshot.members.filter(m => m.id !== (actorId as string))

  // Both of these are null until somebody touches them, and the defaults are
  // DERIVED rather than seeded at mount: the phone reads the local mirror
  // asynchronously, so a set frozen in a useState initialiser is whatever had
  // loaded by the first paint — on a cold start, nobody, permanently.
  const [to, setTo] = useState<MemberId | null>(initialTo ?? null)
  const [text, setText] = useState<string | null>(
    initialAmount !== undefined && initialAmount > 0 ? amountInputValue(initialAmount) : null,
  )
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const suggestedTo = useMemo(() => {
    const owing = snapshot.members.find(
      m => m.id !== (actorId as string) && amountToSettle(ledger, actorId, memberIdOf(m.id)) > 0,
    )
    return owing ? memberIdOf(owing.id) : null
  }, [snapshot.members, ledger, actorId])

  const chosenTo = to ?? suggestedTo
  const owed: Piastres = chosenTo ? amountToSettle(ledger, actorId, chosenTo) : piastres(0)
  const value = text ?? (owed > 0 ? amountInputValue(owed) : '')

  const amount = parseAmount(value)
  const valid = chosenTo !== null && amount !== null && amount > 0
  const remaining = amount === null ? owed : piastres(owed - amount)

  /** The row that sent us here was a simplified transfer, not a pairwise debt. */
  const followingPlan =
    owed <= 0 && chosenTo !== null && chosenTo === initialTo && (initialAmount ?? 0) > 0

  const pick = (id: MemberId) => {
    setTo(id)
    const suggestion = amountToSettle(ledger, actorId, id)
    setText(suggestion > 0 ? amountInputValue(suggestion) : '')
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!valid || busy || chosenTo === null || amount === null) return
    setBusy(true)
    setError(null)
    try {
      await actions.settle({ toMemberId: chosenTo, amount, method })
      onDone?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that payment')
    } finally {
      setBusy(false)
    }
  }

  if (counterparties.length === 0) {
    return (
      <div className="m-screen">
        <h2 className="m-title">Settle up</h2>
        <Empty hint="Settling needs another owner on the ledger. Guests never carry a balance, so they never appear here.">
          There is nobody else to settle with yet.
        </Empty>
      </div>
    )
  }

  return (
    <form className="m-screen" onSubmit={submit} aria-labelledby="settle-title">
      <h2 className="m-title" id="settle-title">Settle up</h2>
      <p className="m-asof">Recording a payment made {formatInstant(now)}</p>

      <fieldset className="m-fieldset">
        <legend className="m-legend">Who are you paying?</legend>
        <div className="m-stack">
          {counterparties.map(m => {
            const id = memberIdOf(m.id)
            const balance = pairBalance(ledger, actorId, id)
            return (
              <label key={m.id} className="m-choice">
                <input
                  type="radio"
                  name="settle-to"
                  value={m.id}
                  checked={chosenTo === id}
                  onChange={() => pick(id)}
                />
                <span className="m-choice-main">{m.displayName}</span>
                <span className="m-choice-side">
                  {balance > 0 ? (
                    <>you owe <Money value={balance} /></>
                  ) : balance < 0 ? (
                    <>owes you <Money value={piastres(-balance)} /></>
                  ) : (
                    'square'
                  )}
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <AmountInput
        id="settle-amount"
        label="Amount (EGP)"
        value={value}
        onChange={setText}
        invalid={value.length > 0 && amount === null}
        hint={
          value.length > 0 && amount === null
            ? 'Enter an amount like 250 or 250.50'
            : chosenTo === null
              ? 'Pick who you are paying'
              : followingPlan
                ? 'The simplified plan routes your payment through them; it squares the ledger overall'
                : owed <= 0
                  ? 'You do not owe them anything — this will put them in your debt'
                  : amount !== null && amount >= owed
                    ? 'Clears the balance'
                    : 'A partial payment; the rest stays outstanding'
        }
      />

      <RadioGroup
        legend="Method"
        name="settle-method"
        options={METHODS}
        value={method}
        onChange={setMethod}
      />

      <p className="m-preview" data-testid="settle-preview">
        {chosenTo === null ? (
          'Pick who you are paying.'
        ) : amount === null ? (
          'Enter an amount to see what it leaves.'
        ) : remaining > 0 ? (
          <>
            After this you will still owe {nameOf(snapshot, chosenTo)} <Money value={remaining} />.
          </>
        ) : remaining === 0 ? (
          <>This clears your balance with {nameOf(snapshot, chosenTo)}.</>
        ) : (
          <>
            This overpays by <Money value={piastres(-remaining)} />; {nameOf(snapshot, chosenTo)} will
            owe you the difference.
          </>
        )}
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="m-actions">
        <button type="submit" className="btn btn-primary" disabled={!valid || busy}>
          Record payment
        </button>
      </div>
    </form>
  )
}
